import { useAuth } from '@/contexts/AuthContext';
import { useResponsive } from '@/hooks/use-responsive';
import { useLanguage } from '@/contexts/LanguageContext';
import { FiCalendar, FiUser, FiMessageCircle, FiInfo, FiChevronDown, FiBell, FiCheckSquare } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { HeaderQrButton } from './HeaderQrButton';
import { NotificationBell } from './notifications/NotificationBell';
import { ThemeToggle } from './ThemeToggle';
import { tourTarget } from '@/features/app-tour';
import './TopNavigation.css';

const NAV_TOUR_IDS: Record<string, string> = {
  eventos: 'nav-eventos',
  chats: 'nav-chats',
  perfil: 'nav-perfil',
  ajustes: 'nav-ajustes',
};

interface NavItem {
  name: string;
  label: string;
  icon: React.ComponentType<{ size?: number; color?: string }>;
  route: string;
}

export function TopNavigation() {
  const { isMobile } = useResponsive();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { hasSuperAdminAccess, hasOrganizerAccess, isAuthenticated } = useAuth();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [hasOrganizer, setHasOrganizer] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const check = async () => {
      if (isAuthenticated) {
        const [superAdmin, organizer] = await Promise.all([hasSuperAdminAccess(), hasOrganizerAccess()]);
        setIsSuperAdmin(superAdmin);
        setHasOrganizer(organizer);
      } else {
        setIsSuperAdmin(false);
        setHasOrganizer(false);
      }
    };
    check();
  }, [isAuthenticated, hasSuperAdminAccess, hasOrganizerAccess]);

  // Close panel dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setPanelOpen(false);
      }
    };
    if (panelOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [panelOpen]);

  const hasPanelAccess = !isMobile && (isSuperAdmin || hasOrganizer);
  const panelOptions = useMemo(() => {
    const opts: { route: string; label: string }[] = [];
    if (isSuperAdmin) opts.push({ route: '/administrador', label: t('navigation.panelAdmin') });
    if (hasOrganizer) opts.push({ route: '/organizer', label: t('navigation.panelOrganizer') });
    return opts;
  }, [isSuperAdmin, hasOrganizer, t]);

  // Configuración de tabs agrupados por bloques (sin Panel: se añade aparte)
  const tabGroups = useMemo(() => {
    const primaryActions: NavItem[] = [
      { name: 'eventos', label: t('navigation.eventos'), icon: FiCalendar, route: '/eventos' },
    ];
    const personalSections: NavItem[] = [
      { name: 'chats', label: t('navigation.chats'), icon: FiMessageCircle, route: '/chats' },
      { name: 'perfil', label: t('navigation.perfil'), icon: FiUser, route: '/perfil' },
    ];
    const infoSections: NavItem[] = [
      { name: 'ajustes', label: t('navigation.ajustes'), icon: FiBell, route: '/ajustes' },
      { name: 'votacion', label: t('navigation.votacion'), icon: FiCheckSquare, route: '/votacion' },
      { name: 'quienes-somos', label: t('navigation.quienesSomos'), icon: FiInfo, route: '/quienes-somos' },
    ];
    return [primaryActions, personalSections, infoSections];
  }, [t]);

  // Obtener la ruta actual
  const currentRoute = location.pathname.split('/').filter(Boolean)[0] || 'eventos';

  // Determinar si una ruta está activa
  const isRouteActive = (tabName: string) => {
    if (tabName === 'eventos') {
      return currentRoute === 'eventos' || currentRoute === '' || location.pathname === '/';
    }
    if (tabName === 'chats') {
      return currentRoute === 'chats' || currentRoute === 'actividad';
    }
    return currentRoute === tabName;
  };

  const isPanelActive = currentRoute === 'administrador' || currentRoute === 'organizer';

  // Solo mostrar en desktop/tablet
  if (isMobile) {
    return null;
  }

  return (
    <nav className={`top-navigation${isPanelActive ? ' top-navigation--panel' : ''}`}>
      <div className="top-navigation-wrapper">
        <div className="top-navigation-content">
          <button
            onClick={() => navigate('/eventos')}
            className="top-navigation-logo"
          >
            BIAVENT
          </button>
          <div className="top-navigation-tabs">
            {tabGroups.map((group, groupIndex) => (
              <React.Fragment key={groupIndex}>
                {group.map((tab) => {
                  const isActive = isRouteActive(tab.name);
                  const Icon = tab.icon;
                  const showIcon = tab.name === 'chats';
                  return (
                    <button
                      key={tab.name}
                      onClick={() => navigate(tab.route)}
                      className={`top-navigation-tab ${isActive ? 'top-navigation-tab-active' : ''}`}
                      {...(NAV_TOUR_IDS[tab.name] ? tourTarget(NAV_TOUR_IDS[tab.name]) : {})}
                    >
                      <span className="top-navigation-tab-text">
                        {showIcon && <Icon size={16} aria-hidden />}
                        {tab.label}
                      </span>
                      <div className={`top-navigation-tab-indicator ${isActive ? 'top-navigation-tab-indicator-active' : ''}`} />
                    </button>
                  );
                })}
              </React.Fragment>
            ))}
            {hasPanelAccess && panelOptions.length > 0 && (
              <div className="top-navigation-panel-wrap" ref={panelRef}>
                  {panelOptions.length === 1 ? (
                    <button
                      type="button"
                      onClick={() => navigate(panelOptions[0].route)}
                      className={`top-navigation-tab ${isPanelActive ? 'top-navigation-tab-active' : ''}`}
                    >
                      <span className="top-navigation-tab-text">
                        {t('navigation.panel')}
                      </span>
                      <div className={`top-navigation-tab-indicator ${isPanelActive ? 'top-navigation-tab-indicator-active' : ''}`} />
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => setPanelOpen((o) => !o)}
                        className={`top-navigation-tab top-navigation-tab-dropdown ${isPanelActive ? 'top-navigation-tab-active' : ''}`}
                        aria-expanded={panelOpen}
                        aria-haspopup="true"
                      >
                        <span className="top-navigation-tab-text">
                          {t('navigation.panel')}
                        </span>
                        <FiChevronDown size={16} style={{ marginLeft: 4, color: 'inherit' }} />
                        <div className={`top-navigation-tab-indicator ${isPanelActive ? 'top-navigation-tab-indicator-active' : ''}`} />
                      </button>
                      {panelOpen && (
                        <div className="top-navigation-panel-dropdown" role="menu">
                          {panelOptions.map((opt) => (
                            <button
                              key={opt.route}
                              type="button"
                              role="menuitem"
                              className="top-navigation-panel-dropdown-item"
                              onClick={() => {
                                navigate(opt.route);
                                setPanelOpen(false);
                              }}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
            )}
          </div>
          <div className="top-navigation-actions">
            <ThemeToggle />
            <HeaderQrButton variant="desktop" />
            <NotificationBell />
          </div>
        </div>
      </div>
    </nav>
  );
}

